/**
 * Reglas de la miga de pan sobre los segmentos que son un id de Mongo.
 *
 * Viven aquí, y no dentro de `Breadcrumbs`, porque las usan dos sitios
 * (`router/labelToRoutes.tsx` al construir la miga y `Breadcrumbs` al
 * sustituir el id por el ROID) y porque así se pueden probar RECORRIENDO
 * FLUJOS enteros, sin montar Redux + Router + i18n alrededor.
 */

/** Un segmento de URL que es un ObjectId de Mongo (`6a5e77ccc6fe1a91238a66d1`). */
export const OBJECT_ID_SEGMENT = /^[0-9a-fA-F]{24}$/;

/** Los ids de Mongo que aparecen en una ruta. */
const routeIds = (pathname: string): string[] =>
  pathname.match(/[0-9a-fA-F]{24}/g) ?? [];

/** ¿La ruta habla de la entidad a la que pertenece este ROID? */
const routeMentions = (pathname: string, roidId: string | null): boolean =>
  !!roidId && routeIds(pathname).includes(roidId);

/**
 * Lo que hay que recordar entre pintado y pintado de la miga.
 *
 * `isExpired` es la respuesta: si está puesto, el ROID que guarda Redux ya no
 * es el de esta pantalla y en su sitio va el hueco de carga.
 */
export type CrumbRoidState = {
  pathname: string;
  roidId: string | null;
  isExpired: boolean;
};

/** Estado de partida: lo que se ve nada más montar la miga. */
export const initialCrumbRoidState = (
  pathname: string,
  roidId: string | null | undefined
): CrumbRoidState => ({ pathname, roidId: roidId ?? null, isExpired: false });

/**
 * ── Por qué esto es una máquina de estados y no una comparación ────────────
 *
 * `roidOTSelected` lo dispatcha cada detalle cuando termina de cargar y NO se
 * limpia al navegar, así que entre pantalla y pantalla conserva el anterior.
 * Antes daba igual porque la navegación no terminaba hasta tener los datos;
 * con los loaders diferidos la miga se pinta primero, y enseñar el ROID de la
 * pantalla anterior no es un retraso: es un ROID que MIENTE sobre lo que hay
 * delante.
 *
 * Tres intentos de resolverlo con una comparación suelta, y por qué fallan:
 *
 * 🔴 «El id del ROID tiene que estar en la URL actual». Una miga enseña
 * ANTEPASADOS a propósito: hay pantallas que dispatchan el ROID del PADRE con
 * el id del padre, que no sale en la URL (editar un accidente desde su tabla).
 * Se quedaban con el hueco para siempre.
 *
 * 🔴 «Comparar con la ruta inmediatamente anterior». El camino normal entre
 * dos detalles pasa por su LISTADO, que no lleva ids: detalle A → lista →
 * detalle B compara B contra la lista, no ve nada, y el ROID de A se cuela.
 *
 * 🔴 «Caduca al salir de su ruta, y hasta que Redux traiga OTRO ROID». Falla
 * al volver a entrar en la MISMA entidad: la pantalla dispatcha el mismo id,
 * el valor no cambia, y la caducidad no se levanta nunca. A partir de la
 * segunda visita esa entidad se queda pillada para siempre — y en cualquier
 * módulo, no solo en Multimedia.
 *
 * ── Las tres reglas, en orden ─────────────────────────────────────────────
 *
 * 1. ROID nuevo    → cuenta desde cero.
 * 2. Salir de su ruta → caduca.
 * 3. Estar en su ruta → revive. Esta es la que faltaba, y es la única que
 *    depende de la RUTA y no del valor, que es justo lo que no cambia al
 *    volver a entrar en la misma entidad.
 */
export const nextCrumbRoidState = (
  previous: CrumbRoidState,
  pathname: string,
  roidIdInput: string | null | undefined
): CrumbRoidState => {
  const roidId = roidIdInput ?? null;

  // 1. Un ROID distinto es otra historia: no hereda la caducidad del anterior.
  let isExpired = roidId === previous.roidId ? previous.isExpired : false;

  // 2. Abandonar la ruta de la entidad del ROID lo caduca.
  if (
    pathname !== previous.pathname &&
    routeMentions(previous.pathname, roidId) &&
    !routeMentions(pathname, roidId)
  ) {
    isExpired = true;
  }

  // 3. Y estar en su ruta lo revive, aunque el valor no haya cambiado.
  if (routeMentions(pathname, roidId)) {
    isExpired = false;
  }

  return { pathname, roidId, isExpired };
};

/**
 * El id del tenant del que habla la ruta, si es que habla de alguno.
 *
 * La miga del detalle de un tenant tiene que enseñar su NOMBRE, y en la URL
 * solo viaja el id: hay que ir a buscarlo. Vive aquí, y no dentro del
 * componente, por lo mismo que el resto de este fichero — para poder probar
 * los bordes (el listado, el alta, otras rutas con un id de Mongo detrás) sin
 * montar Apollo + Router alrededor.
 */
export const adminTenantIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/admin\/tenants\/([0-9a-fA-F]{24})\/?$/);
  return match?.[1] ?? null;
};
